import { useState, useEffect, useCallback, useRef } from "react";
import { useSocket } from "@hooks/useSocket";
import chatApi from "@apis/chat";
import userApi from "@apis/user";
import storeApi from "@apis/store";
import deliverymanApi from "@apis/deliveryman";
import { Helper } from "@utils/index";
import { ChatState, Conversation, Message, ParticipantRole } from "../types";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";

export const useChat = () => {
  const { socket, emit } = useSocket();
  const reduxUser = useSelector((state: RootState) => state.user.data);
  const [currentUser, setCurrentUser] = useState<{
    id: number;
    role: ParticipantRole;
  } | null>(null);

  const [state, setState] = useState<ChatState>({
    conversations: [],
    activeConversationId: null,
    messages: {},
    pagination: {},
    isTyping: {},
    onlineUsers: {},
    directory: [],
    loading: {
      conversations: true,
      messages: false,
      more: false,
    },
  });

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Set current user on mount
  useEffect(() => {
    const user = reduxUser || Helper.getUser();
    if (user) {
      const roleMap: Record<string, ParticipantRole> = {
        admin: "admin",
        store: "store",
        "store-employee": "store",
        delivery: "delivery",
        customer: "customer",
      };
      const resolvedRole = roleMap[user.role] || "admin";

      setCurrentUser({
        id: user.id,
        role: resolvedRole,
        roleId: user.roleId,
        permissions: user.permissions || [],
      });
    }
  }, [reduxUser]);

  const loadConversations = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      loading: { ...prev.loading, conversations: true },
    }));
    try {
      const response = await chatApi.listConversations();
      const conversations = (
        Array.isArray(response) ? response : response?.items || []
      ).sort((a, b) => {
        const hasMsgA = !!a.latestMessage?.id;
        const hasMsgB = !!b.latestMessage?.id;

        if (hasMsgA && !hasMsgB) return -1;
        if (!hasMsgA && hasMsgB) return 1;

        const timeA = new Date(
          (hasMsgA ? a.latestMessage?.createdAt : null) || a.createdAt || 0,
        ).getTime();
        const timeB = new Date(
          (hasMsgB ? b.latestMessage?.createdAt : null) || b.createdAt || 0,
        ).getTime();
        return timeB - timeA;
      });
      setState((prev) => ({
        ...prev,
        conversations,
        loading: { ...prev.loading, conversations: false },
      }));
    } catch (error) {
      console.error("Failed to load conversations", error);
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, conversations: false },
      }));
    }
  }, []);

  const loadMessages = useCallback(async (conversationId: number, page = 1) => {
    const isInitial = page === 1;
    setState((prev) => ({
      ...prev,
      loading: { ...prev.loading, [isInitial ? "messages" : "more"]: true },
    }));

    try {
      const response = await chatApi.listMessages(conversationId, {
        page,
        limit: 30,
      });
      const newMessages = (response?.items || []).reverse();

      setState((prev) => {
        const existingMessages = prev.messages[conversationId] || [];
        const combinedMessages = isInitial
          ? newMessages
          : [...newMessages, ...existingMessages];

        return {
          ...prev,
          messages: { ...prev.messages, [conversationId]: combinedMessages },
          pagination: {
            ...prev.pagination,
            [conversationId]: {
              page: response.page,
              totalPages: response.totalPages,
              hasMore: response.page < response.totalPages,
              unreadCount: response.unreadCount,
            },
          },
          loading: { ...prev.loading, messages: false, more: false },
        };
      });
    } catch (error) {
      console.error("Failed to load messages", error);
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, messages: false, more: false },
      }));
    }
  }, []);

  const loadMoreMessages = useCallback(async () => {
    const conversationId = stateRef.current.activeConversationId;
    if (!conversationId) return;

    const pagination = stateRef.current.pagination[conversationId];
    if (!pagination || !pagination.hasMore || stateRef.current.loading.more)
      return;

    await loadMessages(conversationId, pagination.page + 1);
  }, [loadMessages]);

  const selectConversation = useCallback(
    (conversationId: number) => {
      setState((prev) => ({ ...prev, activeConversationId: conversationId }));

      // Join socket room
      emit("chat:join", { conversationId });

      // Load history if not loaded
      if (!stateRef.current.messages[conversationId]) {
        loadMessages(conversationId);
      }

      // Mark as read
      chatApi.markAsRead(conversationId);
      setState((prev) => ({
        ...prev,
        conversations: prev.conversations.map((c) =>
          c.id === conversationId ? { ...c, unreadCount: 0 } : c,
        ),
      }));
    },
    [emit, loadMessages],
  );

  const sendMessage = useCallback(
    async (content: string, files?: any[]) => {
      const conversationId = stateRef.current.activeConversationId;
      if (!conversationId || !currentUser) return;
      if (!content.trim() && (!files || files.length === 0)) return;

      // Optimistic UI update
      const tempId = -Date.now(); // Negative ID to indicate optimistic
      const messageType = files?.length ? "file" : "text";

      const optimisticMessage: Message = {
        id: tempId,
        conversationId,
        senderId: currentUser.id,
        senderType: currentUser.role === "admin" ? "admin" : "user",
        senderRole: currentUser.role,
        type: messageType,
        content,
        files:
          files?.map((f) => ({
            id: -Math.floor(Math.random() * 1000),
            url: URL.createObjectURL(f),
            fileName: f.name,
            fileType: f.type,
            isLocal: true,
          })) || [],
        metadata: {},
        status: "sending",
        createdAt: new Date().toISOString(),
      };

      setState((prev) => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversationId]: [
            ...(prev.messages[conversationId] || []),
            optimisticMessage,
          ],
        },
        conversations: prev.conversations
          .map((c) =>
            c.id === conversationId
              ? { ...c, latestMessage: optimisticMessage }
              : c,
          )
          .sort((a, b) => {
            const hasMsgA = !!a.latestMessage?.id;
            const hasMsgB = !!b.latestMessage?.id;

            if (hasMsgA && !hasMsgB) return -1;
            if (!hasMsgA && hasMsgB) return 1;

            const timeA = new Date(
              (hasMsgA ? a.latestMessage?.createdAt : null) || a.createdAt || 0,
            ).getTime();
            const timeB = new Date(
              (hasMsgB ? b.latestMessage?.createdAt : null) || b.createdAt || 0,
            ).getTime();
            return timeB - timeA;
          }),
      }));

      try {
        await chatApi.sendMessage(conversationId, {
          content,
          files,
          type: messageType,
        });
      } catch (error: any) {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to send message";
        toast.error(errorMsg);
        console.error("Failed to send message via REST", error);
        // Optional: Update optimistic message to 'error' status
        setState((prev) => ({
          ...prev,
          messages: {
            ...prev.messages,
            [conversationId]: (prev.messages[conversationId] || []).map((m) =>
              m.id === tempId ? { ...m, status: "error" } : m,
            ),
          },
        }));
      }
    },
    [currentUser],
  );

  const startSupportChat = useCallback(async () => {
    try {
      const supportConv = await chatApi.startSupportChat();
      if (supportConv) {
        setState((prev) => {
          const exists = prev.conversations.find(
            (c) => c.id === supportConv.id,
          );
          return {
            ...prev,
            conversations: exists
              ? prev.conversations
              : [supportConv, ...prev.conversations],
            activeConversationId: supportConv.id,
          };
        });
        emit("chat:join", { conversationId: supportConv.id });
        loadMessages(supportConv.id);
      }
      return supportConv;
    } catch (error) {
      console.error("Failed to start support chat", error);
      return null;
    }
  }, [emit, loadMessages]);

  const startDirectChat = useCallback(
    async (participantId: number, role: string) => {
      // 1. Check if we already have this conversation in our active state
      const existing = stateRef.current.conversations.find((c) => {
        if (c.type !== "general") return false;

        // Find other participant that matches target
        const other = c.participants.find(
          (p) =>
            Number(p.participantId) === Number(participantId) &&
            p.role === role &&
            (p.participantId !== currentUser?.id ||
              p.role !== currentUser?.role),
        );

        return !!other && c.participants.length === 2;
      });

      if (existing) {
        selectConversation(existing.id);
        return existing;
      }

      try {
        const response = await chatApi.startGeneralChat(participantId, role);
        const conversation = response?.data || response; // Handle different response wrapper patterns

        if (conversation) {
          setState((prev) => {
            const existsIdx = prev.conversations.findIndex(
              (c) => c.id === conversation.id,
            );
            let newConvs = [...prev.conversations];
            if (existsIdx > -1) {
              newConvs[existsIdx] = { ...newConvs[existsIdx], ...conversation };
            } else {
              newConvs = [conversation, ...prev.conversations];
            }

            return {
              ...prev,
              conversations: newConvs,
              activeConversationId: conversation.id,
            };
          });

          emit("chat:join", { conversationId: conversation.id });
          loadMessages(conversation.id);
        }
        return conversation;
      } catch (error) {
        console.error("Failed to start direct chat", error);
        return null;
      }
    },
    [emit, loadMessages],
  );

  const fetchDirectory = useCallback(async (role: string) => {
    setState((prev) => ({ ...prev, directory: [] }));
    if (role === "all" || role === "order") return;

    try {
      let response: any;
      if (role === "store") {
        response = await storeApi.list({ params: { limit: 100 } });
      } else if (role === "customer") {
        response = await userApi.list({
          params: { role: "customer", limit: 100 },
        });
      } else if (role === "delivery") {
        response = await deliverymanApi.list({ params: { limit: 100 } });
      }
      const rawItems: any[] = Array.isArray(response)
        ? response
        : response?.items || [];
      // Deduplicate by id — backend JOINs can produce multiple rows for the same user
      const seen = new Set<number>();
      const items = rawItems
        .filter((item: any) => {
          if (seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        })
        .map((item: any) => ({ ...item, _dirRole: role }));
      setState((prev) => ({ ...prev, directory: items }));
    } catch (err) {
      console.error("Failed to fetch directory", err);
    }
  }, []);

  const setTyping = useCallback(
    (isTyping: boolean) => {
      const conversationId = stateRef.current.activeConversationId;
      if (!conversationId) return;
      emit("chat:typing", { conversationId, isTyping });
    },
    [emit],
  );

  // Initial load
  useEffect(() => {
    const init = async () => {
      await loadConversations();

      const params = new URLSearchParams(window.location.search);
      if (params.get("type") === "support") {
        const supportConv = await startSupportChat();
        if (supportConv) {
          selectConversation(supportConv.id);
        }
      }

      const userIdParam = params.get("userId");
      const roleParam = params.get("role");
      if (userIdParam && roleParam) {
        // No need to call selectConversation here as startDirectChat handles it
        await startDirectChat(
          Number(userIdParam),
          roleParam as ParticipantRole,
        );
      }
    };

    init();
  }, [loadConversations, selectConversation, startSupportChat]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (message: Message) => {
      console.log({ message });
      const { conversationId } = message;

      setState((prev) => {
        // Update messages list
        const existingMessages = prev.messages[conversationId] || [];

        // Prevent duplicates
        if (existingMessages.find((m) => m.id === message.id)) return prev;

        // Replace optimistic message if it exists, otherwise add new message
        let messageReplaced = false;
        const updatedMessagesList = existingMessages.map((m) => {
          const isMatch =
            m.id < 0 &&
            m.senderId === message.senderId &&
            ((m.content && m.content === message.content) ||
              (m.type === "file" &&
                message.type === "file" &&
                m.files?.length === message.files?.length));

          if (isMatch) {
            messageReplaced = true;
            // Preserve existing status if it's 'delivered' or 'read', otherwise use 'sent'
            const newStatus =
              m.status === "delivered" || m.status === "read"
                ? m.status
                : "sent";
            return { ...message, status: newStatus as typeof m.status };
          }
          return m;
        });

        const finalMessagesList = messageReplaced
          ? updatedMessagesList
          : [...updatedMessagesList, message];

        const updatedMessages = {
          ...prev.messages,
          [conversationId]: finalMessagesList,
        };

        // Update conversation preview and unread count
        const updatedConversations = prev.conversations
          .map((c) => {
            if (c.id === conversationId) {
              const isSelf =
                currentUser?.id === message.senderId &&
                currentUser?.role === message.senderRole;
              const isActive = prev.activeConversationId === conversationId;

              return {
                ...c,
                latestMessage: message,
                unreadCount:
                  (c.unreadCount || 0) + (isActive || isSelf ? 0 : 1),
              };
            }
            return c;
          })
          .sort((a, b) => {
            const hasMsgA = !!a.latestMessage?.id;
            const hasMsgB = !!b.latestMessage?.id;

            if (hasMsgA && !hasMsgB) return -1;
            if (!hasMsgA && hasMsgB) return 1;

            const timeA = new Date(
              (hasMsgA ? a.latestMessage?.createdAt : null) || a.createdAt || 0,
            ).getTime();
            const timeB = new Date(
              (hasMsgB ? b.latestMessage?.createdAt : null) || b.createdAt || 0,
            ).getTime();
            return timeB - timeA;
          });

        // Acknowledge delivery
        if (message.senderId !== currentUser?.id) {
          socket.emit("chat:delivered", {
            conversationId,
            messageId: message.id,
          });
        }

        // Auto-read if active
        if (
          prev.activeConversationId === conversationId &&
          message.senderId !== currentUser?.id
        ) {
          chatApi.markAsRead(conversationId);
        }

        return {
          ...prev,
          messages: updatedMessages,
          conversations: updatedConversations,
        };
      });
    };

    const handleTypingStatus = (data: {
      conversationId: number;
      isTyping: boolean;
    }) => {
      console.log("hsdfs");
      setState((prev) => ({
        ...prev,
        isTyping: { ...prev.isTyping, [data.conversationId]: data.isTyping },
      }));
    };

    const handleUserOnline = (data: {
      id: number;
      role: string;
      key: string;
    }) => {
      setState((prev) => ({
        ...prev,
        onlineUsers: { ...prev.onlineUsers, [data.key]: true },
      }));
    };

    const handleUserOffline = (data: {
      id: number;
      role: string;
      key: string;
    }) => {
      setState((prev) => ({
        ...prev,
        onlineUsers: { ...prev.onlineUsers, [data.key]: false },
      }));
    };

    const handleUsersStatus = (users: any[]) => {
      const onlineMap: Record<string, boolean> = {};
      users.forEach((u) => {
        onlineMap[u.key] = true;
      });
      setState((prev) => ({
        ...prev,
        onlineUsers: { ...prev.onlineUsers, ...onlineMap },
      }));
    };

    const handleReadUpdate = (data: {
      conversationId: number;
      participantId: number;
      lastReadMessageId: number;
      participantType: string;
    }) => {
      setState((prev) => {
        const messages = prev.messages[data.conversationId] || [];
        const updatedMessages = messages.map((m) => {
          if (
            m.id > 0 &&
            m.id <= data.lastReadMessageId &&
            Number(m.senderId) === Number(currentUser?.id)
          ) {
            return { ...m, status: "read" as const };
          }
          return m;
        });

        const updatedConversations = prev.conversations.map((c) => {
          if (c.id === data.conversationId) {
            return {
              ...c,
              participants: c.participants.map((p: any) =>
                Number(p.participantId) === Number(data.participantId) &&
                p.participantType === data.participantType
                  ? { ...p, lastReadMessageId: data.lastReadMessageId }
                  : p,
              ),
            };
          }
          return c;
        });

        return {
          ...prev,
          messages: {
            ...prev.messages,
            [data.conversationId]: updatedMessages,
          },
          conversations: updatedConversations,
        };
      });
    };

    const handleDeliveryUpdate = (data: {
      conversationId: number;
      participantId: number;
      lastDeliveredMessageId: number;
      participantType: string;
    }) => {
      setState((prev) => {
        const messages = prev.messages[data.conversationId] || [];
        const updatedMessages = messages.map((m) => {
          if (
            m.id > 0 &&
            m.id <= data.lastDeliveredMessageId &&
            Number(m.senderId) === Number(currentUser?.id) &&
            m.status !== "read"
          ) {
            return { ...m, status: "delivered" as const };
          }
          return m;
        });

        const updatedConversations = prev.conversations.map((c) => {
          if (c.id === data.conversationId) {
            return {
              ...c,
              participants: c.participants.map((p: any) =>
                Number(p.participantId) === Number(data.participantId) &&
                p.participantType === data.participantType
                  ? {
                      ...p,
                      lastDeliveredMessageId: data.lastDeliveredMessageId,
                    }
                  : p,
              ),
            };
          }
          return c;
        });

        return {
          ...prev,
          messages: {
            ...prev.messages,
            [data.conversationId]: updatedMessages,
          },
          conversations: updatedConversations,
        };
      });
    };

    const handleMessageUpdated = (message: Message) => {
      setState((prev) => {
        const { conversationId } = message;
        const existingMessages = prev.messages[conversationId] || [];
        const updatedMessages = existingMessages.map((m) =>
          m.id === message.id ? message : m,
        );

        return {
          ...prev,
          messages: {
            ...prev.messages,
            [conversationId]: updatedMessages,
          },
        };
      });
    };

    const handleJoined = (enrichedConv: Conversation) => {
      setState((prev) => ({
        ...prev,
        conversations: prev.conversations.map((c) =>
          c?.id === enrichedConv?.id ? { ...c, ...enrichedConv } : c,
        ),
      }));
    };

    socket.on("chat:new_message", handleNewMessage);
    socket.on("chat:message_sent", handleNewMessage); // Handle sender confirmation
    socket.on("chat:message_notification", handleNewMessage); // Handle admins seeing all
    socket.on("chat:typing", handleTypingStatus);
    socket.on("chat:read_update", handleReadUpdate);
    socket.on("chat:delivery_update", handleDeliveryUpdate);
    socket.on("chat:joined", handleJoined);
    socket.on("chat:message_updated", handleMessageUpdated);
    socket.on("user-online", handleUserOnline);
    socket.on("user-offline", handleUserOffline);
    socket.on("users-status", handleUsersStatus);

    return () => {
      socket.off("chat:new_message", handleNewMessage);
      socket.off("chat:message_sent", handleNewMessage);
      socket.off("chat:message_notification", handleNewMessage);
      socket.off("chat:typing", handleTypingStatus);
      socket.off("chat:read_update", handleReadUpdate);
      socket.off("chat:delivery_update", handleDeliveryUpdate);
      socket.off("chat:joined", handleJoined);
      socket.off("chat:message_updated", handleMessageUpdated);
      socket.off("user-online", handleUserOnline);
      socket.off("user-offline", handleUserOffline);
      socket.off("users-status", handleUsersStatus);
    };
  }, [socket, currentUser]);

  return {
    state,
    currentUser,
    selectConversation,
    sendMessage,
    startSupportChat,
    startDirectChat,
    setTyping,
    loadConversations,
    loadMoreMessages,
    fetchDirectory,
  };
};
